import {
    AntCloudOutlined,
    ApiOutlined,
    AppstoreOutlined,
    DashboardOutlined,
    DeploymentUnitOutlined,
    HomeOutlined,
    Loading3QuartersOutlined,
    UserOutlined,
} from '@ant-design/icons'
import { Layout as AntLayout, Button, Col, Grid, Menu, Row, theme, Typography } from 'antd'
import React, { FC, Suspense, useContext, useMemo, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ReportType } from '../../api'
import { AuthContext } from '../../components/auth-provider/AuthProvider'
import { LargeSpinner } from '../../components/large-spinner/LargeSpinner'
import { MAIN_URLS } from '../../URLs'
import './Layout.css'

const { Header, Content, Footer, Sider } = AntLayout
const { useBreakpoint } = Grid

interface LayoutProps {}

export const Layout: FC<LayoutProps> = () => {
    const { xs } = useBreakpoint()
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { isLogged, logOut, user } = useContext(AuthContext)
    const [collapsed, setCollapsed] = useState(true) // Start collapsed by default

    // Update collapsed state when mobile breakpoint changes
    React.useEffect(() => {
        if (xs !== undefined) {
            setCollapsed(xs)
        }
    }, [xs])

    const {
        token: { colorBgContainer },
    } = theme.useToken()

    // Memoized menu items
    const menuItems = useMemo(
        () => [
            {
                label: 'Home',
                icon: <HomeOutlined />,
                onClick: () => navigate(MAIN_URLS.HOME),
                key: MAIN_URLS.HOME,
            },
            {
                label: 'Cultivations',
                icon: <AppstoreOutlined />,
                key: `/${MAIN_URLS.CULTIVATIONS}-header`,
                children: [
                    {
                        label: 'Cultivations',
                        onClick: () => navigate(MAIN_URLS.CULTIVATIONS),
                        key: `/${MAIN_URLS.CULTIVATIONS}`,
                    },
                    {
                        label: 'Advice missing',
                        onClick: () => navigate(`/${MAIN_URLS.REPORT}/${ReportType.ADVICE_MISSING}`),
                        key: `/${MAIN_URLS.REPORT}/${ReportType.ADVICE_MISSING}`,
                    },
                    {
                        label: 'Multiple sensors in zone',
                        onClick: () => navigate(`/${MAIN_URLS.REPORT}/${ReportType.MULTIPLE_SENSORS_IN_ZONE}`),
                        key: `/${MAIN_URLS.REPORT}/${ReportType.MULTIPLE_SENSORS_IN_ZONE}`,
                    },
                    {
                        label: 'Dry moisture status',
                        onClick: () => navigate(`/${MAIN_URLS.REPORT}/${ReportType.DRY_CULTIVATIONS}`),
                        key: `/${MAIN_URLS.REPORT}/${ReportType.DRY_CULTIVATIONS}`,
                    },
                    {
                        label: 'Missing soil info',
                        onClick: () => navigate(`/${MAIN_URLS.REPORT}/${ReportType.MISSING_SOIL_INFORMATION}`),
                        key: `/${MAIN_URLS.REPORT}/${ReportType.MISSING_SOIL_INFORMATION}`,
                    },
                    {
                        label: 'Irrigation advice',
                        onClick: () => navigate(`/${MAIN_URLS.REPORT}/${ReportType.IRRIGATION_ADVICE}`),
                        key: `/${MAIN_URLS.REPORT}/${ReportType.IRRIGATION_ADVICE}`,
                    },
                ],
            },
            {
                label: 'Nodes',
                icon: <DeploymentUnitOutlined />,
                key: `/${MAIN_URLS.NODES}-header`,
                children: [
                    {
                        label: 'Information',
                        onClick: () => navigate(MAIN_URLS.NODES),
                        key: `/${MAIN_URLS.NODES}`,
                    },
                    {
                        label: 'Crop Temperature Nodes',
                        onClick: () => navigate(MAIN_URLS.CROP_TEMPERATURE_NODES),
                        key: `/${MAIN_URLS.CROP_TEMPERATURE_NODES}`,
                    },
                    {
                        label: 'Map',
                        onClick: () => navigate(MAIN_URLS.NODES_MAP),
                        key: `/${MAIN_URLS.NODES_MAP}`,
                    },
                ],
            },
            {
                label: 'Weather Stations',
                icon: <AntCloudOutlined />,
                key: `/${MAIN_URLS.WEATHERSTATIONS}-header`,
                children: [
                    {
                        label: 'Information',
                        onClick: () => navigate(MAIN_URLS.WEATHERSTATIONS),
                        key: `/${MAIN_URLS.WEATHERSTATIONS}`,
                    },
                    {
                        label: 'Map',
                        onClick: () => navigate(MAIN_URLS.WEATHERSTATIONS_MAP),
                        key: `/${MAIN_URLS.WEATHERSTATIONS_MAP}`,
                    },
                ],
            },
            {
                label: 'Farms',
                icon: <UserOutlined />,
                onClick: () => navigate(MAIN_URLS.FARMS),
                key: `/${MAIN_URLS.FARMS}`,
            },
            {
                label: 'Firmwares',
                icon: <ApiOutlined />,
                onClick: () => navigate(MAIN_URLS.FIRMWARES),
                key: `/${MAIN_URLS.FIRMWARES}`,
            },
            {
                label: 'OTA requests',
                icon: <Loading3QuartersOutlined />,
                onClick: () => navigate(MAIN_URLS.OTA_REQUESTS),
                key: `/${MAIN_URLS.OTA_REQUESTS}`,
            },
            {
                label: 'Node Status',
                icon: <DashboardOutlined />,
                onClick: () => navigate(MAIN_URLS.NODE_STATUS),
                key: `/${MAIN_URLS.NODE_STATUS}`,
            },
        ],
        [navigate]
    )

    // Auto-determine which dropdown should be open based on current path
    const getDefaultOpenKeys = () => {
        if (pathname.includes(MAIN_URLS.CULTIVATIONS) || pathname.includes(MAIN_URLS.REPORT)) {
            return [`/${MAIN_URLS.CULTIVATIONS}-header`]
        }
        if (pathname.includes(MAIN_URLS.NODES)) {
            return [`/${MAIN_URLS.NODES}-header`]
        }
        if (pathname.includes(MAIN_URLS.WEATHERSTATIONS)) {
            return [`/${MAIN_URLS.WEATHERSTATIONS}-header`]
        }
        return []
    }

    const logo = (
        <Typography.Title level={4} className="layout__title">
            Dashboard
        </Typography.Title>
    )

    console.log(pathname)

    return (
        <AntLayout>
            <Header className="layout__header">
                <Row gutter={16} justify="space-between" align="middle" className="layout__header-row">
                    <Col>{isLogged ? <Link to={MAIN_URLS.HOME}>{logo}</Link> : logo}</Col>
                    {isLogged && (
                        <Col>
                            {!xs && <span className="layout__user">{user}</span>}
                            <Button onClick={logOut}>Log out</Button>
                        </Col>
                    )}
                </Row>
            </Header>
            <AntLayout>
                {isLogged && (
                    <Sider
                        className="layout__menu"
                        theme="light"
                        collapsible
                        collapsed={collapsed}
                        onCollapse={setCollapsed}
                        width={200}
                        collapsedWidth={80}
                    >
                        <Menu
                            theme="dark"
                            mode="inline"
                            selectedKeys={[pathname]}
                            defaultOpenKeys={getDefaultOpenKeys()}
                            items={menuItems}
                        />
                    </Sider>
                )}
                <Content
                    className={`layout__content ${isLogged ? (collapsed ? 'layout__content--collapsed' : 'layout__content--expanded') : 'layout__content--not-logged-in'}`}
                >
                    <div className="layout__container">
                        <Suspense fallback={<LargeSpinner />}>
                            <Outlet />
                        </Suspense>
                    </div>
                </Content>
            </AntLayout>
            <Footer />
        </AntLayout>
    )
}
